import { useSlideEditorContext } from "@/components/slides/editor/SlideEditorContext";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { updateSlide } from "@/store/slideEditorSlice";
import type { PhonicsDecodeSlideData } from "@ai-teach/types";
import { IconButton } from "@ai-teach/ui-web";
import { useEffect, useRef, useState } from "react";
import { FaPause, FaPlay } from "react-icons/fa";
import { HiX } from "react-icons/hi";
import { EditorContainer } from "../../EditorContainer";

const EMPTY_AUDIO = { src: null, id: null, filename: null };

function buildDefaultDecodeData(): PhonicsDecodeSlideData {
  return { audio: EMPTY_AUDIO, phoneme: null, phonemeId: null };
}

export function PhonicsDecodeEditor() {
  const dispatch = useAppDispatch();
  const { slide } = useAppSelector((state) => state.slideEditor.present);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const { setActivePanel } = useSlideEditorContext();
  const slideData = (slide?.slideData ?? buildDefaultDecodeData()) as PhonicsDecodeSlideData;
  const { audio, phoneme } = slideData;
  const slideName = slide?.name ?? "";

  const update = (patch: Partial<PhonicsDecodeSlideData>) => {
    if (!slide) return;
    dispatch(updateSlide({ slideId: slide.id, slideData: { ...slideData, ...patch } }));
  };

  const handleRemoveAudio = () => {
    update({ audio: EMPTY_AUDIO, phonemeId: null });
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const handlePlayPause = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-attach listeners when audio source changes
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onTime = () => setCurrentTime(el.currentTime);
    const onMeta = () => setDuration(el.duration);
    const onEnd = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("loadedmetadata", onMeta);
    el.addEventListener("ended", onEnd);
    return () => {
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("loadedmetadata", onMeta);
      el.removeEventListener("ended", onEnd);
    };
  }, [audio.src]);

  const formatTime = (t: number) => {
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <EditorContainer>
      {/* Header */}
      <div className="flex items-center justify-between shrink-0 px-3">
        <h2 className="text-sm font-semibold text-primary-900 truncate uppercase tracking-widest">
          {slideName || "Spelling to Sound"}
        </h2>
        <span className="text-[10px] font-medium text-gray-100 bg-primary-900 px-2 py-0.5 rounded-full shrink-0">
          Spelling to Sound
        </span>
      </div>

      <div className="flex-1 overflow-y-auto flex flex-col gap-3">
        {/* Sound — reference audio and the phoneme text shown to the student */}
        <div className="bg-surface rounded-lg border border-border overflow-hidden">
          <div className="px-3 py-1.5 bg-muted/40 border-b border-border">
            <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-widest">
              Sound
            </span>
          </div>

          {audio.src && (
            // biome-ignore lint/a11y/useMediaCaption: phonics audio is pronunciation teaching
            <audio ref={audioRef} src={audio.src} preload="metadata" />
          )}

          <div className="p-3 space-y-3">
            <div className="min-h-[64px] flex flex-col justify-center gap-2">
              {audio.src ? (
                <>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handlePlayPause}
                      className="shrink-0 w-8 h-8 rounded-full bg-blue-500 hover:bg-blue-600 flex items-center justify-center transition-all hover:scale-105 active:scale-95"
                    >
                      {isPlaying ? (
                        <FaPause className="text-white text-[10px]" />
                      ) : (
                        <FaPlay className="text-white text-[10px] ml-0.5" />
                      )}
                    </button>
                    <p className="flex-1 text-xs font-medium text-foreground truncate">
                      {audio.filename || "Reference audio"}
                    </p>
                    <IconButton variant="danger" onClick={handleRemoveAudio}>
                      <HiX className="text-xs" />
                    </IconButton>
                  </div>
                  <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 rounded-full transition-all duration-100"
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-[9px] text-muted-foreground">
                    <span>{formatTime(currentTime)}</span>
                    <span>{formatTime(duration)}</span>
                  </div>
                </>
              ) : (
                <p className="text-xs font-medium text-muted-foreground text-center">
                  No audio selected
                </p>
              )}
            </div>

            <button
              type="button"
              onClick={() => setActivePanel("Phonics")}
              className="w-full py-1.5 text-xs font-semibold text-white bg-blue-500 hover:bg-blue-600 rounded-md transition-colors"
            >
              {audio.src ? "Change sound" : "Browse Phonics Library"}
            </button>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground block">Phoneme</label>
              <input
                type="text"
                value={phoneme ?? ""}
                onChange={(e) => update({ phoneme: e.target.value || null })}
                placeholder='e.g. "th", "ai"'
                className="w-full px-3 py-1.5 bg-surface border border-border rounded text-sm font-medium focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
              />
              <p className="text-[11px] text-muted-foreground">
                Shown to the student so they know what to pronounce.
              </p>
            </div>
          </div>
        </div>

        {/* Info box */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <p className="text-[11px] font-semibold text-amber-700 mb-1">How it works</p>
          <p className="text-[11px] text-amber-600 leading-relaxed">
            Students see the phoneme text and can play the reference audio, then record themselves
            pronouncing it. Azure pronunciation assessment scores their accuracy.
          </p>
        </div>
      </div>
    </EditorContainer>
  );
}
